import React, { useContext, useEffect, useState, useRef } from 'react'
import IconWithBackground from '../IconWithBackground/IconWithBackground'
import { getMessageThread, sendMessage } from '../../services/api/messages'
import DefaultLoader from '../Loaders/DefaultLoader'
import Avatar from '../Avatar/Avatar'
import IconButton from '../Button/IconButton'
import { OrderMessageThread, OrderMessageThreadMessage } from '../../model'
import MessageBlock from './MessageBlock'
import { formatDate, formatTime, isToday } from '../../helpers/utils'
import { TibiContext } from '../../context/ContextProvider'
import { NavLink } from 'react-router-dom'
import DynamicFaIcon from '../DynamicFaIcon/DynamicFaIcon'

export interface MessageThreadProps {
    threadId: number | null
    onSend?: () => void
    className?: string
}

const MessageThread: React.FunctionComponent<MessageThreadProps> = ({
    threadId,
    onSend,
    className
}: MessageThreadProps) => {
    const { value }: any = useContext(TibiContext)
    const username: string = value?.user?.username

    const [threadData, setThreadData] = useState<OrderMessageThread | null>(null)
    const [threadMessages, setThreadMessages] = useState<OrderMessageThreadMessage[]>([])
    const [newMessage, setNewMessage] = useState<string>('')
    const [loading, setLoading] = useState<boolean>(false)
    const textareaRef = useRef<HTMLTextAreaElement>(null)

    const isPackage = false
    let prevDate: string | null | undefined = null

    const sender = threadData?.concerns

    useEffect(() => {
        if (threadId) {
            getMessageThreadData()
        }
        // eslint-disable-next-line
    }, [threadId])

    // disabled for unnatural feeling
    // useEffect(() => {
    //     const element = document.getElementById('lastMessage')
    //     element?.scrollIntoView()
    // }, [threadMessages])

    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto'
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
        }
    }, [newMessage])

    const getMessageThreadData = async () => {
        setLoading(true)
        try {
            const params = { pageSize: 1000 }
            const data = await getMessageThread(threadId, params)
            setThreadData(data.thread)
            setThreadMessages(data.items)
        } catch (e) {
            console.error(e)
        }
        setLoading(false)
    }

    const send = async () => {
        if (newMessage) {
            const id = await sendMessage(threadId, { message: newMessage })
            setThreadMessages([
                // @ts-ignore
                ...threadMessages,
                {
                    id: id,
                    message: newMessage,
                    sender: username,
                    // @ts-ignore
                    date: new Date(),
                    self: true
                }
            ])
            setNewMessage('')
            onSend?.()
        }
    }

    return (
        <div
            className={
                'flex justify-center w-full h-full rounded-t-xl lg:rounded-tl-none rounded-b-xl lg:rounded-bl-none bg-white ' +
                className
            }
        >
            {loading ? (
                <DefaultLoader />
            ) : threadId ? (
                <div className='flex flex-col relative w-full rounded-xl'>
                    <div className='rounded-t-xl'>
                        <header className='flex items-center p-3 border-btm max-h-[63px] gap-x-3 bg-white rounded-tl-xl md:rounded-tl-none rounded-tr-xl'>
                            {/* Back button */}
                            {/* @ts-ignore */}
                            <NavLink to={-1} className='flex lg:hidden'>
                                <DynamicFaIcon iconName='chevron-left' />
                            </NavLink>
                            {
                                // @ts-ignore
                                threadData?.order?.icon ? (
                                    <IconWithBackground
                                        iconName={
                                            // @ts-ignore
                                            threadData?.order?.icon
                                        }
                                    />
                                ) : (
                                    <Avatar name={sender} invert />
                                )
                            }
                            <div>
                                <h4 className='font-medium'>{sender}</h4>
                            </div>
                        </header>
                    </div>

                    <div className='overflow-y-auto flex-grow rounded-b-xl bg-[#fdfdfd] shadow-sm'>
                        {threadMessages.map((message: OrderMessageThreadMessage, i: number) => {
                            const formattedDate = formatDate(message.date, {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit'
                            })
                            const showDate = prevDate !== formattedDate
                            prevDate = formattedDate

                            const formattedTime = formatTime(message.date)

                            return (
                                <div
                                    key={message.id}
                                    className='flex flex-col'
                                    {...(i === threadMessages.length - 1 && { id: 'lastMessage' })}
                                >
                                    {showDate && (
                                        <div className='flex justify-center my-4 text-xs font-bold uppercase opacity-50'>
                                            {isToday(message.date) ? 'Vandaag' : formattedDate}
                                        </div>
                                    )}

                                    <div className='w-full'>
                                        <MessageBlock
                                            sender={message.sender}
                                            text={message.message}
                                            self={message.self}
                                            time={formattedTime}
                                            key={message.id}
                                        />
                                    </div>
                                </div>
                            )
                        })}
                    </div>

                    <div className='flex w-full p-4 border-top rounded-b-xl gap-2 bg-white bottom-0 items-center h-[76px] shrink-0'>
                        <textarea
                            ref={textareaRef}
                            name={'message_' + threadId}
                            className='border grow text-[--tb-text] py-1 px-2 resize-none rounded-md max-h-40 outline-none overflow-y-auto'
                            placeholder='Schrijf hier je bericht. Gebruik SHIFT + ENTER voor een nieuwe regel.'
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault()
                                    send()
                                }
                            }}
                            rows={1}
                        />
                        <div className='flex justify-center rotate-12'>
                            <IconButton iconName='paper-plane' onClick={send} iconClassName='pr-1' />
                        </div>
                    </div>
                </div>
            ) : (
                <div className='self-center flex flex-col my-auto items-center'>
                    <DynamicFaIcon className='text-[--tb-primary] text-2xl mb-1' iconName='message' />
                    <p className='text-gray-500'>Hier verschijnen de berichten die je verstuurt en ontvangt.</p>
                </div>
            )}
        </div>
    )
}

export default MessageThread
